<script>
   /****************************************************************/
   /* LoadingsPlot                                                 */
   /* --------------------                                         */
   /* shows plot with PCA loadings                                 */
   /****************************************************************/

   import { Axes, XAxis, YAxis, Box, Segments } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, AXIS_COLOR } from './Defaults.js';
   import { Vector, vector } from 'mdatools/arrays';

   import PlotSeries from './PlotSeries.svelte';
   import { pcamodel_get } from '../wasm/dist/pca.js';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let params;                        // JSON with user defined plot parameters
   export let model;                         // JSON with PCA model object

   // saving parameters
   export let fileName = 'plot-loadings';
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;

   // outputs
   export let selected = undefined;          // selected variable (for future implementations)


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // constant plot parameters
   const showGrid = true;

   /**
    * Gets vector with values for particular outcome and component from the model object
    * @param {Object} m - model object.
    * @param {Number} a - component index (from 1);
    */
   function getValues(m, a) {
      return new Vector(pcamodel_get(m, 'loadings', a))
   }

   // reactive expression to generate x-indices for line and bar plots
   let varIndices, varValues, varLabels, varvaluesUnits, varvaluesName, varlabels
   $: if (model) {
      varvaluesName = model.varvaluesName;
      varvaluesUnits = model.varvaluesUnits;
      varValues = model.varvalues;
      varIndices = vector(model.varindices);
      varLabels = model.varlabels;
   }

   /**
    * Event handler to select a data point.
    * @param e - event object.
    */
   function select(e) {
      // TODO the selection of variables is off for now, to be implemented later
      return;
      if (selected === undefined) return; // do not select points if this option is not activated in parent app
      const id = parseInt(e.detail.elementID)
      const series_title = e.detail.seriesTitle
      if (id >= 0 && (series_title === 'loadings-points' || series_title === 'loadings-bars')) {
         selected = id;
      }
   }

   /**
    * Event handler to remove selection.
    * @param e - event object.
    */
   function resetSelection(e) {
      selected = -1;
   }


   // ranges of values to define axis limits
   let yMax = Number.NEGATIVE_INFINITY;
   let yMin = Number.POSITIVE_INFINITY;
   let xMax = Number.NEGATIVE_INFINITY;
   let xMin = Number.POSITIVE_INFINITY;

   // local copies of changeable plot parameters
   let comp = [1, 1];
   let plotType = 'p';
   let showLabels = 'hide';
   let axesScale = 'normal';

   // reactive expressions to monitor changes in plot parameters
   $: if (params && params.comp !== comp) comp = params.comp;
   $: if (params && params.plotType !== plotType) plotType = params.plotType;
   $: if (params && params.showLabels !== showLabels) showLabels = params.showLabels;
   $: if (params && params.axesScale !== axesScale) axesScale = params.axesScale;


   // reactive expression to track chenges in "res" and "comp[0]" (x-axis)
   let compX = 0, compXValues = null;
   $: if (model && comp && comp[0] !== compX) {
      compX = comp[0];
      compXValues = getValues(model, compX);
   }

   // reactive expression to track chenges in "reobj" and "comp[1]" (y-axis)
   let compY = 0, yValues = null;
   $: if (model && comp && comp[1] !== compY) {
      compY = comp[1];
      yValues = getValues(model, compY);
   }

   // switch between scores and indices for x-axis
   let xValues, xLabel, yLabel;
   $: if (plotType === 'p') {
      xValues = compXValues;
      xLabel = `PC${compX}`;
      yLabel = `PC${compY}`;
   } else if (plotType === 'l' && varValues.length > 0) {
      xValues = vector(varValues);
      xLabel = varvaluesName + (varvaluesUnits ? ', ' + varvaluesUnits : '');
      yLabel = `PC${compY}`;
   } else {
      xValues = varIndices;
      xLabel = 'Variables';
      yLabel = `PC${compY}`;
   }

   // reactive expression to compute y-axis limits
   let limYc;
   $: if (yMin < Number.POSITIVE_INFINITY && yMax > Number.NEGATIVE_INFINITY) {
      const dY = (yMax - yMin) * 0.10;
      limYc = [yMin - dY, yMax + dY];
   } else {
      limYc = [0, 1];
   }

   // reactive expression to compute x-axis limits
   let limXc;
   $: if (xMin < Number.POSITIVE_INFINITY && xMax > Number.NEGATIVE_INFINITY) {
      const dX = (xMax - xMin) * 0.10;
      limXc = [xMin - dX, xMax + dX];
   } else {
      limXc = [0, 1];
   }

   let limX, limY;
   $: if (axesScale === 'equal') {
      if (plotType === 'p') {
         const v = Math.max(Math.abs(limXc[0]), Math.abs(limYc[0]), limXc[1], limYc[1]) * 1.05;
         limX = [-v, v];
         limY = [-v, v];
      } else {
         const v = Math.max(Math.abs(limYc[0]), limYc[1]) * 1.05;
         limX = limXc;
         limY = [-v, v];
      }
   } else {
      limX = limXc;
      limY = limYc;
   }
</script>

{#if model }
<Axes
   {xLabel} {yLabel} {limX} {limY}
   {fileName} {pngWidth} {pngHeight} {pngRes}
   on:markerclick={select}
   on:barclick={select}
   on:axesclick={resetSelection}
   title="Loadings"
   downloadLinks="hover"
   margins={MARGINS}
>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   {#if plotType === 'p'}
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {xValues}
      {yValues}
      {plotType}
      {showLabels}
      {selected}
      seriesTitle="loadings"
      labels={varLabels}
      bind:minYVal={yMin}
      bind:maxYVal={yMax}
      bind:minXVal={xMin}
      bind:maxXVal={xMax}
   />

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
{/if}
